import React, { useCallback, useEffect, useMemo, useState } from 'react';
import BaseButton from '../form/BaseButton.jsx';
import BaseOverlay from '../layout/BaseOverlay.jsx';
import BaseAutocomplete from '../form/BaseAutocomplete.jsx';
import BaseDropdown from '../form/BaseDropdown.jsx';
import { InputNumber } from 'primereact/inputnumber';
import { useDebounce } from '../../hooks/useDebounce.js';
import { useLazyGetCompaniesQuery } from '../../api/contact.js';
import { useLazyGetCalendarContactsQuery } from '../../api/calendar.js';
import IconClose from '../images/IconClose.jsx';
import BaseTreeSelect from '../form/BaseTreeSelect.jsx';
import IconCancel from '../images/IconCancel.jsx';
import { useGetProjectCountriesQuery, useGetSkillsQuery } from '../../api/misc.js';
import { MultiSelect } from 'primereact/multiselect';

const AdvancedSearchFilterProjects = ({
    filters,
    setFilters,
    visible,
    setVisible,
    applyFilters,
    setApplied,
    statusesList,
    statusesLoading,
    selectedNeeds,
    setSelectedNeeds,
    selectedCapabilities,
    setSelectedCapabilities
}) => {
    const [companySearch, setCompanySearch] = useState('');
    const [contactSearch, setContactSearch] = useState('');
    const [companyList, setCompanyList] = useState([]);
    const [contactList, setContactList] = useState([]);
    const debouncedCompanySearch = useDebounce(companySearch, 500);
    const debouncedContactSearch = useDebounce(contactSearch, 500);

    const [getCompanies, { isLoading: isLoadingCompanies }] = useLazyGetCompaniesQuery();
    const [getContacts, { isLoading: isLoadingContacts }] = useLazyGetCalendarContactsQuery();

    const treeElement = useCallback((key, item) => {
        return {
            key,
            label: item.name,
            data: item.id,
            ...(item.sub_skills && item.sub_skills.length > 0
                ? {
                      children: item.sub_skills.map((sub_skill, index) => {
                          return treeElement(`${key}-${index}`, sub_skill);
                      })
                  }
                : {})
        };
    }, []);

    const { data: skillsData } = useGetSkillsQuery({});
    const skills = useMemo(() => {
        let skillOptions = [];
        if (skillsData?.response?.skills) {
            let skillsArr = skillsData.response.skills;
            skillsArr.map((item, index) => {
                skillOptions.push(treeElement(index, item));
            });
        }

        return skillOptions;
    }, [skillsData, treeElement]);

    const { data: countriesData } = useGetProjectCountriesQuery({});
    const countriesList = useMemo(() => {
        if (countriesData?.response?.project_countries) {
            return countriesData.response.project_countries.map((country) => ({
                label: country.name,
                value: country.id
            }));
        }

        return [];
    }, [countriesData]);

    const addCompany = (id, name) => {
        const newCompany = { id, name };
        const updatedCompanies = filters.companies ? [...filters.companies, newCompany] : [newCompany];
        setFilters((prev) => ({ ...prev, companies: updatedCompanies }));
        setCompanySearch('');
    };

    const removeCompany = (id) => {
        const updatedCompanies = filters.companies.filter((company) => company.id !== id);
        setFilters((prev) => ({ ...prev, companies: updatedCompanies }));
    };

    const addContact = (id, name) => {
        const newContact = { contact_id: id, name };
        const updatedContacts = filters.contacts ? [...filters.contacts, newContact] : [newContact];
        setFilters((prev) => ({ ...prev, contacts: updatedContacts }));
        setContactSearch('');
    };

    const removeContact = (id) => {
        const updatedContacts = filters.contacts.filter((contact) => contact.contact_id !== id);
        setFilters((prev) => ({ ...prev, contacts: updatedContacts }));
    };

    useEffect(() => {
        if (debouncedCompanySearch) {
            (async () => {
                const result = await getCompanies({ search: debouncedCompanySearch });
                setCompanyList(result?.data?.response?.companies || []);
            })();
        } else {
            setCompanyList([]);
        }
    }, [debouncedCompanySearch, getCompanies]);

    useEffect(() => {
        if (debouncedContactSearch) {
            (async () => {
                const result = await getContacts({ search: debouncedContactSearch });
                setContactList(result?.data?.response?.contacts || []);
            })();
        } else {
            setContactList([]);
        }
    }, [debouncedContactSearch, getContacts]);

    const hideDialog = () => {
        setVisible(false);
    };

    const filter = () => {
        applyFilters();
        setApplied(true);
        hideDialog();
    };

    const removeTreeSelect = (field, setSelectedTreeValues) => {
        setSelectedTreeValues({});
        setFilters((prev) => ({ ...prev, [field]: [] }));
    };
    const handleTreeSelectChange = (field, value, options, setSelectedTreeValues) => {
        setSelectedTreeValues(value);

        let arr = [];
        Object.entries(value).forEach(([key, type]) => {
            let split = key.split('-');
            let element = options[split[0]];

            if (split.length > 1) {
                split.slice(1).forEach((index) => {
                    element = element.children[index];
                });
            }

            if (type.checked) {
                arr.push(element.data);
            }
        });

        setFilters((prev) => ({ ...prev, [field]: arr }));
    };

    return (
        <BaseOverlay show={visible} className='overflow-auto h-[100dvh] rounded-none'>
            <div className='min-h-96 max-w-3xl w-full mx-auto p-4'>
                <div className='flex justify-end py-4'>
                    <div className='text-theme1-primary font-bold cursor-pointer' onClick={hideDialog}>
                        Close
                    </div>
                </div>
                <div className='text-center text-theme1-title2 font-bold text-[22px] mb-3'>Filter</div>
                <div className='text-left'>
                    <div className='mb-[30px]'>
                        <h2 className='pb-[10px] text-xl font-bold'>Status</h2>
                        <BaseDropdown
                            id='status'
                            name='status'
                            placeholder='Select status'
                            value={filters.status}
                            options={statusesList}
                            setValue={(value) => setFilters((prev) => ({ ...prev, status: value }))}
                            isLoading={statusesLoading}
                        />
                    </div>
                    <div className='mb-[30px]'>
                        <h2 className='pb-[10px] text-xl font-bold'>Reach</h2>
                        <div className='flex items-center'>
                            <MultiSelect
                                value={filters.reach}
                                onChange={(e) =>
                                    setFilters((prev) => ({
                                        ...prev,
                                        reach: e.value
                                    }))
                                }
                                options={countriesList}
                                optionLabel='label'
                                filter
                                placeholder={'Select countries'}
                                maxSelectedLabels={3}
                                className={`bg-transparent border-theme1-text3 border rounded-3xl px-2 ${
                                    filters.reach.length > 0 ? 'w-[90%]' : 'w-full'
                                }`}
                            />

                            {filters.reach.length > 0 && (
                                <div
                                    className='cursor-pointer w-[36px] flex-shrink-0'
                                    onClick={() =>
                                        setFilters((prev) => ({
                                            ...prev,
                                            reach: []
                                        }))
                                    }
                                >
                                    <IconCancel className='fill-theme1-primary' />
                                </div>
                            )}
                        </div>
                    </div>

                    <div className='mb-[30px]'>
                        <h2 className='pb-[10px] text-xl font-bold'>Companies</h2>
                        <BaseAutocomplete
                            inputClassName='text-theme1-text1 rounded-2xl border px-4 py-2 border-theme1-text3 w-full outline-none'
                            items={companyList.map((company) => ({
                                label: company.name,
                                value: company.id
                            }))}
                            onSelect={(id, label) => addCompany(id, label)}
                            value={companySearch}
                            setValue={setCompanySearch}
                            placeholder='Search for a company...'
                            isLoading={isLoadingCompanies}
                            exclude={filters.companies ? filters.companies.map((company) => company.id) : []}
                        />
                    </div>

                    {filters.companies && filters.companies.length > 0 && (
                        <div className='flex flex-wrap gap-2 mb-[30px] -mt-4'>
                            {filters.companies?.map((company) => (
                                <span
                                    key={company.id}
                                    className='flex items-center gap-2 bg-theme1-gold1 pl-3 pr-2 py-1 rounded-4xl text-white'
                                >
                                    <span>{company.name}</span>
                                    <button
                                        type='button'
                                        className='flex items-center justify-center p-1 rounded focus:outline-none'
                                        onClick={() => removeCompany(company.id)}
                                    >
                                        <IconClose className='fill-white w-4 h-4' />
                                    </button>
                                </span>
                            ))}
                        </div>
                    )}

                    <div className='mb-[30px]'>
                        <h2 className='pb-[10px] text-xl font-bold'>Contacts</h2>
                        <BaseAutocomplete
                            inputClassName='text-theme1-text1 rounded-2xl border px-4 py-2 border-theme1-text3 w-full outline-none'
                            items={contactList.map((contact) => ({
                                label: `${contact.first_name} ${contact.last_name}`,
                                value: contact.id
                            }))}
                            onSelect={(id, label) => addContact(id, label)}
                            value={contactSearch}
                            setValue={setContactSearch}
                            placeholder='Search for a contact...'
                            isLoading={isLoadingContacts}
                            exclude={filters.contacts ? filters.contacts.map((contact) => contact.contact_id) : []}
                        />
                    </div>

                    {filters.contacts && filters.contacts.length > 0 && (
                        <div className='flex flex-wrap gap-2 mb-[30px] -mt-4'>
                            {filters.contacts?.map((contact) => (
                                <span
                                    key={contact.contact_id}
                                    className='flex items-center gap-2 bg-theme1-gold1 pl-3 pr-2 py-1 rounded-4xl text-white'
                                >
                                    <span>{contact.name}</span>
                                    <button
                                        type='button'
                                        className='flex items-center justify-center p-1 rounded focus:outline-none'
                                        onClick={() => removeContact(contact.contact_id)}
                                    >
                                        <IconClose className='fill-white w-4 h-4' />
                                    </button>
                                </span>
                            ))}
                        </div>
                    )}

                    <div className='mb-[30px]'>
                        <h2 className='pb-[10px] text-xl font-bold'>Budget</h2>
                        <div className='flex gap-4 flex-col sm:flex-row'>
                            <InputNumber
                                value={filters.budget?.min || ''}
                                onValueChange={(e) =>
                                    setFilters((prev) => ({
                                        ...prev,
                                        budget: { ...prev.budget, min: e.value }
                                    }))
                                }
                                placeholder='Min Budget'
                                className='w-full bg-transparent border-theme1-text3 border rounded-4xl px-4 py-2'
                            />
                            <InputNumber
                                value={filters.budget?.max || ''}
                                onValueChange={(e) =>
                                    setFilters((prev) => ({
                                        ...prev,
                                        budget: { ...prev.budget, max: e.value }
                                    }))
                                }
                                placeholder='Max Budget'
                                className='w-full bg-transparent border-theme1-text3 border rounded-4xl px-4 py-2'
                            />
                        </div>
                    </div>

                    <div className='mb-[30px]'>
                        <h2 className='pb-[10px] text-xl font-bold'>Actual Costs</h2>
                        <div className='flex gap-4 flex-col sm:flex-row'>
                            <InputNumber
                                value={filters.actual_costs?.min || ''}
                                onValueChange={(e) =>
                                    setFilters((prev) => ({
                                        ...prev,
                                        actual_costs: { ...prev.actual_costs, min: e.value }
                                    }))
                                }
                                placeholder='Min Actual Costs'
                                className='w-full bg-transparent border-theme1-text3 border rounded-4xl px-4 py-2'
                            />
                            <InputNumber
                                value={filters.actual_costs?.max || ''}
                                onValueChange={(e) =>
                                    setFilters((prev) => ({
                                        ...prev,
                                        actual_costs: { ...prev.actual_costs, max: e.value }
                                    }))
                                }
                                placeholder='Max Actual Costs'
                                className='w-full bg-transparent border-theme1-text3 border rounded-4xl px-4 py-2'
                            />
                        </div>
                    </div>

                    <div className={'mb-[30px] leading-tight'} key={'needs'}>
                        <h2 className='pb-[10px] text-xl font-bold'>Needs</h2>
                        <div className='flex items-center gap-2'>
                            <BaseTreeSelect
                                name={'Needs'}
                                label={'Select Needs'}
                                placeholder={'Select Needs'}
                                inputClassName='md:w-20rem w-full rounded-full h-[48px]'
                                value={selectedNeeds}
                                setValue={setSelectedNeeds}
                                options={skills}
                                selectionMode='checkbox'
                                display='chip'
                                showClear={false}
                                filter={true}
                                filterMode={'strict'}
                                filterDelay={10}
                                onSelect={(data) => handleTreeSelectChange('needs', data, skills, setSelectedNeeds)}
                            />

                            {filters.needs?.length > 0 && (
                                <div
                                    className='cursor-pointer w-[36px] flex-shrink-0'
                                    onClick={() => removeTreeSelect('needs', setSelectedNeeds)}
                                >
                                    <IconCancel className='fill-theme1-primary' />
                                </div>
                            )}
                        </div>
                    </div>

                    <div className={'mb-[30px] leading-tight'} key={'capabilities'}>
                        <h2 className='pb-[10px] text-xl font-bold'>Capabilities</h2>
                        <div className='flex items-center gap-2'>
                            <BaseTreeSelect
                                name={'Capabilities'}
                                label={'Select Capabilities'}
                                placeholder={'Select Capabilities'}
                                inputClassName='md:w-20rem w-full rounded-full h-[48px]'
                                value={selectedCapabilities}
                                setValue={setSelectedCapabilities}
                                options={skills}
                                selectionMode='checkbox'
                                display='chip'
                                showClear={false}
                                filter={true}
                                filterMode={'strict'}
                                filterDelay={10}
                                onSelect={(data) =>
                                    handleTreeSelectChange('capabilities', data, skills, setSelectedCapabilities)
                                }
                            />

                            {filters.capabilities?.length > 0 && (
                                <div
                                    className='cursor-pointer w-[36px] flex-shrink-0'
                                    onClick={() => removeTreeSelect('capabilities', setSelectedCapabilities)}
                                >
                                    <IconCancel className='fill-theme1-primary' />
                                </div>
                            )}
                        </div>
                    </div>

                    <div className='flex justify-between gap-4 w-full'>
                        <BaseButton
                            className='bg-theme1-primary text-theme1-white flex-shrink-0'
                            textValue='Apply Filters'
                            onClick={filter}
                        />
                    </div>
                </div>
            </div>
        </BaseOverlay>
    );
};

export default AdvancedSearchFilterProjects;
